import {
  InfoChip, InspectionHeader, InspectionInfo, InspectionRowBody, InspectionRowCard,
} from '@components/ProjectOverview/Inspections/Inspections.styled';
import { useLocalization } from '@lib/localization';
import { buildProjectPath } from '@lib/routing/projectPath';
import { noticeLabelKeys } from '@lib/spproject/inspectionStatus';
import { Box, LinearProgress, Paper, Tooltip, Typography } from '@mui/material';
import type { RecentInspection } from '@store/api/statisticEndpoints';
import useNoticeColors from '@store/hooks/useNoticeColors';
import { Calendar, ListFilter, User } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

interface RecentInspectionsProps {
  data: RecentInspection[] | undefined;
  /** Rendered at the far right of the heading — where Home puts the way out to the page. */
  action?: ReactNode;
  /**
   * The rows on screen are on their way out: another page has been asked for and hasn't
   * arrived. They stay where they are — emptying the list would collapse it and shift
   * everything below — and are dimmed under a thin bar instead.
   */
  loading?: boolean;
}

/**
 * The inspections filed most recently, drawn as the project's own inspection list draws
 * them — same card, same chips, same hover — so the two read as one kind of thing.
 *
 * What differs is what Home knows: there are no points of interest behind these rows, so
 * the status is the inspection's own rather than a breakdown, and the project it belongs to
 * takes the place the id holds inside a project, where the project is not a question.
 *
 * Home shows the handful the statistics preview; the page behind the heading shows the same
 * rows a page at a time. The list itself doesn't know which it is drawing — it is handed
 * rows — so the two can't drift apart.
 */
const RecentInspectionsList: React.FC<RecentInspectionsProps> = ({ data, action, loading = false }) => {
  const { t } = useLocalization();
  const noticeColors = useNoticeColors();
  const navigate = useNavigate();

  const rows = data ?? [];

  /**
   * Straight to the inspection's own page. The folder prefix is left out on purpose: it is
   * context, the page repairs it in place once the project resolves, and asking for it here
   * would cost a lookup per row for a URL that already works without it.
   */
  const openInspection = (row: RecentInspection) =>
    navigate(buildProjectPath({ projectId: row.projectId, inspectionId: row.inspectionId }));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ListFilter size={22} />
          <Typography variant="h6" sx={{ ml: 1, fontWeight: 300 }}>
            {t('Statistic_RecentInspections')}
          </Typography>
        </Box>

        {action}
      </Box>

      <Box sx={{ position: 'relative' }}>
        {/* In the gap under the heading rather than in the flow, so it appearing and going
            moves nothing. */}
        {loading && (
          <LinearProgress sx={{ position: 'absolute', top: -10, left: 0, right: 0, height: 2, borderRadius: 1 }} />
        )}

        <Box aria-busy={loading} sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 150ms' }}>
          {rows.map((row) => (
            <InspectionRowCard key={row.inspectionId} onClick={() => openInspection(row)}>
              <InspectionRowBody>
                <InspectionHeader sx={{ flexGrow: 1, minWidth: 0, py: 1.5, minHeight: 48 }}>
                  <InspectionInfo sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                      {row.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {row.projectName}
                    </Typography>
                  </InspectionInfo>

                  {/* One line, as on the inspection list: the name to the left truncates rather
                      than pushing these onto a second row and growing the card. */}
                  <Box sx={{
                    display: 'flex', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end',
                    flexShrink: 0, gap: 1,
                  }}>
                    {/* A dot and its name, the way `InspectionStatusSummary` shows a status on
                        the inspection list: this row already carries two chips, and a filled
                        third one was the loudest thing on it. The colour is carried by the dot
                        and the meaning by the word, so neither stands alone. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{
                        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                        backgroundColor: noticeColors[row.status],
                      }} />
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {t(noticeLabelKeys[row.status] ?? 'Common_Unknown')}
                      </Typography>
                    </Box>

                    {row.author && (
                      <Tooltip title={t('Inspection_UploadedBy')}>
                        <InfoChip
                          icon={<User size={14} style={{ marginRight: '1px' }} />}
                          label={row.author.name}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}

                    <Tooltip title={t('Common_DateUpdated')}>
                      <InfoChip
                        icon={<Calendar size={14} style={{ marginRight: '1px' }} />}
                        label={new Date(row.dateModified).toAgoString()}
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  </Box>
                </InspectionHeader>
              </InspectionRowBody>
            </InspectionRowCard>
          ))}

          {rows.length === 0 && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('Inspections_NotFound')}</Typography>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RecentInspectionsList;
